"use client";

import { ActionButton } from "../ActionButton/ActionButton";
import { MuxPlayer } from "../MuxPlayer/MuxPlayer";
import classNames from "classnames/bind";
import Image from "next/image";
import { ReactNode, useEffect, useState } from "react";
import styles from "./FeaturedMedia.module.css";

const cx = classNames.bind(styles);
const FEATURED_MEDIA_PLAY_EVENT = "featured-media:play";

interface FeaturedMediaProps {
  mediaType?: "image" | "video" | null;
  image?: {
    src: string;
    alt: string;
  } | null;
  videoPlaybackId?: string | null;
  videoTitle?: string | null;
  videoThumbnailTimestamp?: number | null;
  className?: string;
  children?: ReactNode;
}

const MUX_POSTER_WIDTH = 1280;

const getMuxThumbnailTime = (time?: number | null) =>
  typeof time === "number" && Number.isFinite(time) && time >= 0
    ? time
    : undefined;

const getMuxPosterUrl = (playbackId: string, time?: number) => {
  const params = new URLSearchParams({
    width: String(MUX_POSTER_WIDTH),
  });

  if (time != null) {
    params.set("time", String(time));
  }

  return `https://image.mux.com/${encodeURIComponent(playbackId)}/thumbnail.webp?${params}`;
};

export const FeaturedMedia = ({
  mediaType,
  image,
  videoPlaybackId,
  videoTitle,
  videoThumbnailTimestamp,
  className,
  children,
}: FeaturedMediaProps) => {
  const [isPaused, setIsPaused] = useState(true);
  const [instanceId] = useState(
    () => `featured-media-${Math.random().toString(36).slice(2)}`,
  );

  const announcePlayback = () => {
    if (typeof window === "undefined") {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(FEATURED_MEDIA_PLAY_EVENT, {
        detail: { instanceId },
      }),
    );
  };

  const playVideo = () => {
    announcePlayback();
    setIsPaused(false);
  };

  const togglePlayback = () => {
    if (isPaused) {
      playVideo();
      return;
    }

    setIsPaused(true);
  };

  const hasVideo = mediaType === "video" && Boolean(videoPlaybackId);
  const hasImage = Boolean(image?.src) && !hasVideo;
  const thumbnailTime = getMuxThumbnailTime(videoThumbnailTimestamp);
  const videoPoster = videoPlaybackId
    ? getMuxPosterUrl(videoPlaybackId, thumbnailTime)
    : undefined;

  useEffect(() => {
    setIsPaused(true);
  }, [mediaType, videoPlaybackId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const pauseOnOtherPlayback = (event: Event) => {
      const customEvent = event as CustomEvent<{ instanceId?: string }>;

      if (customEvent.detail?.instanceId !== instanceId) {
        setIsPaused(true);
      }
    };

    window.addEventListener(FEATURED_MEDIA_PLAY_EVENT, pauseOnOtherPlayback);

    return () => {
      window.removeEventListener(
        FEATURED_MEDIA_PLAY_EVENT,
        pauseOnOtherPlayback,
      );
    };
  }, [instanceId]);

  if (!hasVideo && !hasImage) {
    return null;
  }

  return (
    <div className={cx("root", className)}>
      <div
        className={cx("frame", {
          "is-video": hasVideo,
        })}
        onClick={hasVideo ? togglePlayback : undefined}
      >
        {hasVideo ? (
          <>
            <MuxPlayer
              playbackId={videoPlaybackId || ""}
              className={cx("player")}
              paused={isPaused}
              poster={videoPoster}
              thumbnailTime={thumbnailTime}
              metadata={{
                videoTitle: videoTitle || undefined,
              }}
              onPlay={() => {
                announcePlayback();
                setIsPaused(false);
              }}
              onPause={() => setIsPaused(true)}
              onEnded={() => setIsPaused(true)}
            />
            <ActionButton
              variant="dark"
              size="small"
              className={cx("controlButton")}
              onClick={(event) => {
                event.stopPropagation();
                togglePlayback();
              }}
              aria-label={isPaused ? "Play video" : "Pause video"}
            >
              {isPaused ? "Play" : "Pause"}
            </ActionButton>
          </>
        ) : (
          <Image
            src={image?.src || ""}
            alt={image?.alt || ""}
            fill
            sizes="(min-width: 1024px) 1120px, 100vw"
            className={cx("image")}
          />
        )}
        {children ? <div className={cx("overlay")}>{children}</div> : null}
      </div>
    </div>
  );
};
